/**
 * Katalog KATEGORII powiadomień — jedno źródło prawdy dla ekranu preferencji użytkownika
 * (`NotificationCenterPage`, zakładka „Preferencje") i dla panelu administratora
 * (`admin/NotificationSettingsPage`).
 *
 * DLACZEGO OSOBNY PLIK: obie strony grupują typy po `typeCategory` z backendu i obie miały
 * WŁASNĄ, niepełną mapę kolorów. Kategoria, której w mapie nie było, dostawała szary kolor
 * i lądowała na końcu w kolejności przypadkowej (kolejność kluczy obiektu) — więc dwie nowe
 * rodziny („Do akceptacji", „Odpowiedzi"), o które prosił właściciel produktu, wylądowałyby
 * na samym dole listy siedemdziesięciu typów. Kolejność i opisy trzymamy więc w jednym miejscu.
 *
 * KATEGORIE POCHODZĄ Z BAZY (`NotificationTypes.Category`) — ten plik ich NIE definiuje, tylko
 * porządkuje. Kategoria nieznana temu plikowi nadal się wyświetli: dostanie kolor domyślny
 * i miejsce na końcu, alfabetycznie. To celowe — nowy typ dosiany w `DbInitializer` ma się
 * pojawić w obu panelach BEZ zmiany we froncie.
 */

/** Kategoria zbiorcza „coś czeka na moją decyzję" (DbInitializer: KategoriaDoAkceptacji). */
export const KATEGORIA_AKCEPTACJE = "Do akceptacji";

/** Kategoria zbiorcza „ktoś odpowiedział na to, co napisałem" (DbInitializer: KategoriaOdpowiedzi). */
export const KATEGORIA_ODPOWIEDZI = "Odpowiedzi";

/**
 * Kody typów należących do rodziny „Do akceptacji".
 *
 * Kategoria z bazy jest źródłem prawdy dla GRUPOWANIA, ale kropka w menu i licznik na pulpicie
 * dostają pojedyncze powiadomienie, a nie listę typów — potrzebują więc listy kodów. Lista jest
 * zgodna z `DbInitializer.SeedAkceptacjeIOdpowiedziNotificationTypesAsync`
 * + `UjednolicKategorieAkceptacjiAsync`.
 */
export const KODY_AKCEPTACJI = [
    "hr_pending_approval",
    // [zgłoszenie właściciela 2026-09-03, poz. 1c] Ten sam wniosek na etapie KADROWYM. Osobny kod,
    // bo kropka w menu i odnośnik prowadzą do /obsluga-hr, a nie do „Mojego Działu".
    "hr_pending_approval_kadry",
    "vacation_pending",
    "vacation_plan_pending",
    "remote_work_pending",
    "terms_acceptance_required",
    "itam_protocol_issued",
    "pl_item_done_requested",
    "pl_access_requested",
    "registration_submitted",
    "drive_access_requested",
    "approvals_digest",
    // [POLITYKI-poprawka 2026-09-29, E7 D31] Runda przeglądu/zarządu polityki — typ siany w kategorii „Do akceptacji”
    // (DbInitializer.Polityki: KategoriaDoAkceptacji); bez wpisu tutaj zdarzenie approvals-changed nie szłoby po tym powiadomieniu.
    "polityka_do_zatwierdzenia",
];

/** Kody typów rodziny „Odpowiedzi" (komentarze, wzmianki, wiadomość sygnalisty). */
export const KODY_ODPOWIEDZI = [
    "comment_reply",
    "comment_on_content",
    "comment_mention",
    "sygnalista_new_message",
];

/**
 * Kolejność wyświetlania kategorii. Dwie rodziny wymagane przez właściciela produktu stoją
 * NA GÓRZE — po to, żeby odpowiedź na pytanie „gdzie się to wyłącza" była jednym spojrzeniem,
 * a nie przewijaniem listy do końca.
 */
export const KOLEJNOSC_KATEGORII = [
    KATEGORIA_AKCEPTACJE,
    KATEGORIA_ODPOWIEDZI,
    "Zgłoszenia",
    "HR",
    "Wnioski HR",
    "Kadry",
    /* [PAKIET 2026-09-02] Trzy nowe rodziny sieje `SeedFundamentNotificationTypesAsync`. Stoją TUŻ
       przy „HR"/„Kadry", bo pracownik szuka ich tam, gdzie reszta spraw kadrowych — a nie na końcu
       listy stu typów, gdzie wylądowałyby bez wpisu (kategoria nieznana katalogowi idzie na koniec).
       Nazwy MUSZĄ być znak w znak takie, jak `category` w seederze — literówka = rodzina poza katalogiem. */
    "Cele",
    "Dokumenty",
    "Oferty pracy",
    "Moj Dzial",
    "Kalendarz",
    "Zadania",
    "Kanban",
    "Projekty",
    "Dostępy",
    "Szkolenia",
    "Komunikacja",
    "Compliance",
    "System",
];

/** Kolor akcentu kategorii (kropka nagłówka, ikona typu). */
export const KOLOR_KATEGORII = {
    [KATEGORIA_AKCEPTACJE]: "#6366F1",
    [KATEGORIA_ODPOWIEDZI]: "#0EA5E9",
    "Zgłoszenia": "#3B82F6",
    "HR": "#EC4899",
    "Wnioski HR": "#EC4899",
    "Kadry": "#DB2777",
    "Cele": "#EAB308",
    "Dokumenty": "#0891B2",
    "Oferty pracy": "#A855F7",
    "Moj Dzial": "#F97316",
    "Kalendarz": "#22C55E",
    "Zadania": "#0EA5E9",
    "Kanban": "#8B5CF6",
    "Projekty": "#6366F1",
    "Dostępy": "#10B981",
    "Szkolenia": "#14B8A6",
    "Komunikacja": "#F59E0B",
    "Compliance": "#6366F1",
    "System": "#EF4444",
};

/** Kolor dla kategorii spoza katalogu — neutralny, ale nadal czytelny w obu motywach. */
export const KOLOR_DOMYSLNY = "#6B7280";

export const kolorKategorii = (nazwa) => KOLOR_KATEGORII[nazwa] || KOLOR_DOMYSLNY;

/**
 * Opis kategorii „po ludzku" — zdanie, które ma pracownikowi powiedzieć, czego dokładnie
 * przestanie dostawać, jeśli wyłączy pozycje z tej grupy. Bez tego przełącznik przy nazwie
 * technicznej („pl_access_requested") jest decyzją w ciemno.
 */
export const OPIS_KATEGORII = {
    [KATEGORIA_AKCEPTACJE]:
        "Sprawy, które czekają na Twoją decyzję: wnioski HR, urlopy i plany urlopowe, praca zdalna, "
        + "prośby o dostęp, pozycje projektów zgłoszone jako zrobione, regulaminy i protokoły sprzętu.",
    [KATEGORIA_ODPOWIEDZI]:
        "Ktoś odpowiedział na to, co napisałeś: odpowiedź na Twój komentarz, komentarz pod Twoją treścią, "
        + "wzmianka @login oraz nowa wiadomość w prowadzonej przez Ciebie sprawie sygnalisty.",
    "Zgłoszenia": "Zgłoszenia do obsługi i Twoje własne: nowe, przypisane, odpowiedzi i notatki.",
    "HR": "Decyzje w Twoich sprawach kadrowych i przypomnienia z modułu HR.",
    "Wnioski HR": "Obieg wniosków kadrowych — statusy, korekty, realizacja.",
    "Kadry": "Sprawy prowadzone przez dział kadr.",
    "Cele": "Twoje cele: przypisanie, akceptacja propozycji, zbliżający się termin i ocena okresu.",
    "Dokumenty": "Dokumenty wydane na Ciebie lub przez Ciebie: umowy i pisma kadrowe, pisma działu, podpisane skany.",
    "Oferty pracy": "Nabory wewnętrzne: nowe ogłoszenia w Twoim zasięgu, status Twojej aplikacji i Twoich poleceń.",
    "Moj Dzial": "Sprawy Twojego zespołu widoczne dla przełożonego.",
    "Kalendarz": "Urlopy, grafik pracy i wydarzenia w kalendarzu.",
    "Zadania": "Zadania przypisane, udostępnione i zbliżające się terminy.",
    "Kanban": "Tablice Kanban — karty, terminy, komentarze.",
    "Projekty": "Listy projektowe — dodanie do projektu, przypisanie pozycji, terminy.",
    "Dostępy": "Dostępy do dysków sieciowych, projektów i kont.",
    "Szkolenia": "E-learning: przypisane kursy, terminy, wyniki.",
    "Komunikacja": "Komunikaty firmowe, aktualności, przypomnienia z czatu.",
    "Compliance": "Sprawy zgodności i sygnalisty.",
    "System": "Techniczne komunikaty aplikacji.",
};

export const opisKategorii = (nazwa) => OPIS_KATEGORII[nazwa] || "";

/**
 * Sortuje nazwy kategorii wg `KOLEJNOSC_KATEGORII`; nieznane idą na koniec, alfabetycznie
 * (locale PL — inaczej „Ł" i „Ż" lądują poza porządkiem).
 */
export const sortujKategorie = (nazwy) =>
    [...nazwy].sort((a, b) => {
        const ia = KOLEJNOSC_KATEGORII.indexOf(a);
        const ib = KOLEJNOSC_KATEGORII.indexOf(b);
        if (ia !== -1 && ib !== -1) return ia - ib;
        if (ia !== -1) return -1;
        if (ib !== -1) return 1;
        return String(a).localeCompare(String(b), "pl");
    });

/**
 * Grupuje listę typów po `typeCategory` i zwraca pary [kategoria, pozycje] w kolejności
 * katalogu. Wspólne dla obu ekranów — dotąd każdy grupował po swojemu.
 */
export const pogrupujPoKategorii = (pozycje, pobierzKategorie = (p) => p.typeCategory) => {
    const mapa = new Map();
    (pozycje || []).forEach((p) => {
        const kat = pobierzKategorie(p) || "Pozostałe";
        if (!mapa.has(kat)) mapa.set(kat, []);
        mapa.get(kat).push(p);
    });
    return sortujKategorie([...mapa.keys()]).map((kat) => [kat, mapa.get(kat)]);
};
